import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import config from '../config/config';
import Skeleton from './common/Skeleton';
import EmptyState from './common/EmptyState';
import { authFetch, authUrl } from '../utils/api';
import { buildSbomCandidates } from '../utils/sbom';
import Pagination, { DEFAULT_PAGE_SIZE } from './Pagination';

function getPURLType(purl) {
  const m = purl && purl.match(/^pkg:([^/]+)\//);
  return m ? m[1] : null;
}

function getDistroFromPURL(purl) {
  const m = purl && purl.match(/[?&]distro=([^&]+)/);
  if (!m) return null;
  return m[1]
    .replace(/^alpine-/, 'Alpine ')
    .replace(/^debian-/, 'Debian ')
    .replace(/^ubuntu-/, 'Ubuntu ');
}

const SEVERITY_CONFIG = {
  CRITICAL: { bg: 'danger',   icon: '🔴', order: 0, text: 'white' },
  HIGH:     { bg: 'warning',  icon: '🟠', order: 1, text: 'dark' },
  MEDIUM:   { bg: 'info',     icon: '🟡', order: 2, text: 'dark' },
  LOW:      { bg: 'secondary',icon: '🔵', order: 3, text: 'white' },
  UNKNOWN:  { bg: 'light',    icon: '⬜', order: 4, text: 'dark' },
};

const REACH_BADGE = {
  reachable:           { cls: 'bg-danger',  icon: '⚠️', label: 'Reachable' },
  not_affected:        { cls: 'bg-success', icon: '✅', label: 'Not affected' },
  under_investigation: { cls: 'bg-secondary', icon: '🔵', label: 'Investigating' },
};

const SeverityBadge = ({ severity }) => {
  return (
    <span className={`ce-sev ce-sev--${(severity || 'unknown').toLowerCase()}`}>{severity || 'UNKNOWN'}</span>
  );
};

/** Small reachability indicator shown inline in the CVE table */
const ReachBadge = ({ status }) => {
  if (!status) return <span className="text-muted small">—</span>;
  const cfg = REACH_BADGE[status];
  if (!cfg) return <span className="text-muted small">—</span>;
  return (
    <span className={`badge ${cfg.cls}`} style={{ fontSize: '0.65rem' }} title={`Reachability: ${status}`}>
      {cfg.icon} {cfg.label}
    </span>
  );
};

/* ─── CVE Detail Slide-Over Panel ─────────────────────────────────────────── */

const panelOverlayStyle = {
  position: 'fixed', inset: 0,
  background: 'rgba(0,0,0,0.35)', zIndex: 1049,
};

const panelDrawerStyle = {
  position: 'fixed', top: 0, right: 0,
  height: '100vh', width: 480, maxWidth: '95vw',
  background: 'white', boxShadow: '-4px 0 24px rgba(0,0,0,0.12)',
  zIndex: 1050, overflowY: 'auto', padding: 24,
};

const sectionHeadStyle = {
  fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.08em',
  textTransform: 'uppercase', color: '#94a3b8', marginBottom: 8,
  paddingBottom: 4, borderBottom: '1px solid #f1f5f9',
};

const inputStyle = {
  width: '100%', padding: '6px 10px', border: '1px solid #e2e8f0',
  borderRadius: 6, fontSize: '0.85rem', background: '#f8fafc',
  boxSizing: 'border-box',
};

const selectStyle = { ...inputStyle };

const btnPrimaryStyle = {
  padding: '7px 18px', borderRadius: 6, border: 'none', cursor: 'pointer',
  background: '#6366f1', color: 'white', fontWeight: 600, fontSize: '0.82rem',
};

function CveDetailPanel({ vuln, repoName, sbomId, reachMap, onClose }) {
  const [vex, setVex] = useState({ status: 'In Triage', justification: '' });
  const [rem, setRem] = useState({ status: 'open', owner: '', dueDate: '', notes: '' });
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const [jiraLoading, setJiraLoading] = useState(false);
  const [jiraResult, setJiraResult] = useState(null);

  if (!vuln) return null;

  const reachStatus = reachMap[vuln.vulnId] || reachMap[vuln.aliases?.[0]];
  const reachCfg = reachStatus ? REACH_BADGE[reachStatus] : null;

  async function saveVex() {
    setSaving(true); setMsg('');
    try {
      const res = await authFetch(
        `${config.BASE_URL}/sbom/${repoName}/${sbomId}-sbom.json/vex/${vuln.vulnId}`,
        { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(vex) }
      );
      setMsg(res.ok ? '✅ VEX statement saved.' : `❌ Error: ${(await res.json()).error || res.status}`);
    } catch (e) { setMsg(`❌ ${e.message}`); }
    setSaving(false);
  }

  async function saveRem() {
    setSaving(true); setMsg('');
    try {
      const res = await authFetch(
        `${config.BASE_URL}/sbom/${repoName}/${sbomId}-sbom.json/remediation/${vuln.vulnId}`,
        { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(rem) }
      );
      setMsg(res.ok ? '✅ Remediation tracked.' : `❌ Error: ${(await res.json()).error || res.status}`);
    } catch (e) { setMsg(`❌ ${e.message}`); }
    setSaving(false);
  }

  async function createJira() {
    setJiraLoading(true); setJiraResult(null);
    try {
      const res = await authFetch(
        `${config.BASE_URL}/sbom/${repoName}/${sbomId}-sbom.json/remediation/${vuln.vulnId}/jira`,
        { method: 'POST' }
      );
      const body = await res.json();
      setJiraResult(res.ok ? { ok: true, key: body.key, url: body.url } : { ok: false, error: body.error || res.status });
    } catch (e) { setJiraResult({ ok: false, error: e.message }); }
    setJiraLoading(false);
  }

  return (
    <>
      <div style={panelOverlayStyle} onClick={onClose} />
      <div style={panelDrawerStyle} role="dialog" aria-modal="true">
        {/* Close */}
        <button
          onClick={onClose}
          style={{ position: 'absolute', top: 14, right: 16, background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#94a3b8' }}
          title="Close"
        >✕</button>

        {/* Header */}
        <div style={{ marginBottom: 20 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 6 }}>
            <span style={{ fontSize: '1.25rem', fontWeight: 800, color: '#1e293b' }}>{vuln.vulnId}</span>
            <SeverityBadge severity={vuln.severity} />
            {vuln.cvssScore != null && (
              <span style={{ fontSize: '0.85rem', fontWeight: 700, color: '#475569', background: '#f1f5f9', padding: '2px 10px', borderRadius: 20 }}>
                CVSS {vuln.cvssScore.toFixed(1)}
              </span>
            )}
          </div>
          {reachCfg && (
            <span className={`badge ${reachCfg.cls}`} style={{ fontSize: '0.7rem', marginBottom: 6, display: 'inline-block' }}>
              {reachCfg.icon} {reachCfg.label}
            </span>
          )}
          {vuln.aliases && vuln.aliases.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
              {vuln.aliases.map(a => (
                <span key={a} className="badge bg-light text-dark" style={{ fontSize: '0.7rem' }}>{a}</span>
              ))}
            </div>
          )}
        </div>

        {/* Summary */}
        <div style={{ marginBottom: 20 }}>
          <div style={sectionHeadStyle}>Summary</div>
          <p style={{ fontSize: '0.85rem', color: '#334155', lineHeight: 1.55, margin: 0 }}>{vuln.summary || '—'}</p>
        </div>

        {/* Affected package */}
        <div style={{ marginBottom: 20 }}>
          <div style={sectionHeadStyle}>Affected Package</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
            {[
              { label: 'Package', value: vuln.packageName },
              { label: 'Version', value: vuln.version || '—' },
              { label: 'Fixed in', value: vuln.fixedIn || 'No fix available' },
            ].map(({ label, value }) => (
              <div key={label} style={{ background: '#f8fafc', borderRadius: 8, padding: '8px 12px' }}>
                <div style={{ fontSize: '0.65rem', color: '#94a3b8', fontWeight: 700, textTransform: 'uppercase' }}>{label}</div>
                <code style={{ fontSize: '0.8rem', color: '#1e293b', wordBreak: 'break-all' }}>{value}</code>
              </div>
            ))}
          </div>
        </div>

        {/* VEX Statement */}
        <div style={{ marginBottom: 20 }}>
          <div style={sectionHeadStyle}>Add VEX Statement</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <select style={selectStyle} value={vex.status} onChange={e => setVex(v => ({ ...v, status: e.target.value }))}>
              {['Exploitable', 'Not Exploitable', 'In Triage', 'Fixed', 'Under Investigation'].map(s => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
            <textarea
              style={{ ...inputStyle, minHeight: 72, resize: 'vertical' }}
              placeholder="Justification (optional)"
              value={vex.justification}
              onChange={e => setVex(v => ({ ...v, justification: e.target.value }))}
            />
            <button style={btnPrimaryStyle} onClick={saveVex} disabled={saving}>
              {saving ? '…' : 'Save VEX'}
            </button>
          </div>
        </div>

        {/* Remediation Tracking */}
        <div style={{ marginBottom: 20 }}>
          <div style={sectionHeadStyle}>Track Remediation</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <select style={selectStyle} value={rem.status} onChange={e => setRem(r => ({ ...r, status: e.target.value }))}>
              {['open', 'in_progress', 'resolved', 'accepted_risk', 'suppressed'].map(s => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
            <input
              style={inputStyle} placeholder="Owner (e.g. team or user)"
              value={rem.owner} onChange={e => setRem(r => ({ ...r, owner: e.target.value }))}
            />
            <input
              type="date" style={inputStyle}
              value={rem.dueDate} onChange={e => setRem(r => ({ ...r, dueDate: e.target.value }))}
            />
            <textarea
              style={{ ...inputStyle, minHeight: 60, resize: 'vertical' }}
              placeholder="Notes"
              value={rem.notes} onChange={e => setRem(r => ({ ...r, notes: e.target.value }))}
            />
            <button style={btnPrimaryStyle} onClick={saveRem} disabled={saving}>
              {saving ? '…' : 'Save Remediation'}
            </button>
          </div>
        </div>

        {/* Jira */}
        <div style={{ marginBottom: 20 }}>
          <div style={sectionHeadStyle}>Jira Integration</div>
          <button
            style={{ ...btnPrimaryStyle, background: '#0052cc' }}
            onClick={createJira}
            disabled={jiraLoading}
          >
            {jiraLoading ? '⏳ Creating…' : '🎫 Create Jira Ticket'}
          </button>
          {jiraResult && (
            <div style={{ marginTop: 8, fontSize: '0.82rem' }}>
              {jiraResult.ok
                ? <span>✅ Created <a href={jiraResult.url} target="_blank" rel="noreferrer">{jiraResult.key}</a></span>
                : <span style={{ color: '#dc2626' }}>❌ {jiraResult.error}</span>}
            </div>
          )}
        </div>

        {msg && (
          <div style={{
            padding: '8px 12px', borderRadius: 6, marginTop: 8, fontSize: '0.82rem',
            background: msg.startsWith('✅') ? '#f0fdf4' : '#fef2f2',
            color: msg.startsWith('✅') ? '#166534' : '#991b1b',
            border: `1px solid ${msg.startsWith('✅') ? '#bbf7d0' : '#fecaca'}`,
          }}>
            {msg}
          </div>
        )}
      </div>
    </>
  );
}

/* ─── Main Component ───────────────────────────────────────────────────────── */

const VulnerabilityView = ({ repoName, sbomId, sbomName, onDataLoaded, initialData }) => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [data, setData] = useState(initialData || null);
  const [loading, setLoading] = useState(!initialData);
  const [error, setError] = useState('');
  // Reachability map: vulnId/osvId → reachability status
  const [reachMap, setReachMap] = useState({});

  // Detail panel
  const [selectedVuln, setSelectedVuln] = useState(null);

  // Bulk selection
  const [selected, setSelected] = useState(new Set());
  const [bulkLoading, setBulkLoading] = useState(false);
  const [bulkMsg, setBulkMsg] = useState('');

  const filter = searchParams.get('vuln_filter') || 'ALL';
  const search = searchParams.get('vuln_search') || '';
  const page = Math.max(1, parseInt(searchParams.get('vuln_page') || '1', 10));
  const pageSize = parseInt(localStorage.getItem('depguard_page_size') || String(DEFAULT_PAGE_SIZE), 10);

  const setVulnFilter = (val) => setSearchParams(prev => { const n = new URLSearchParams(prev); n.set('vuln_filter', val); n.set('vuln_page', '1'); return n; });
  const setVulnSearch = (val) => setSearchParams(prev => { const n = new URLSearchParams(prev); n.set('vuln_search', val); n.set('vuln_page', '1'); return n; });
  const setVulnPage   = (val) => setSearchParams(prev => { const n = new URLSearchParams(prev); n.set('vuln_page', String(val)); return n; });

  useEffect(() => {
    if (initialData !== undefined) return; // parent controls data
    if (!repoName || !sbomId) return;
    setLoading(true);
    setError('');
    axios
      .get(`${config.BASE_URL}/sbom/${repoName}/${sbomId}-sbom.json/vulnerabilities`)
      .then(res => { setData(res.data); if (onDataLoaded) onDataLoaded(); })
      .catch(err => setError(err.response?.data?.error || err.message || t('vulnerabilities.loadError')))
      .finally(() => setLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [repoName, sbomId]);

  useEffect(() => {
    if (initialData === undefined) return;
    setData(initialData || null);
    setLoading(false);
    if (initialData && onDataLoaded) onDataLoaded();
  }, [initialData]); // eslint-disable-line react-hooks/exhaustive-deps

  // Background load reachability data (non-blocking — best effort)
  useEffect(() => {
    if (!repoName || !sbomId) return;
    let cancelled = false;
    const candidates = buildSbomCandidates(sbomId, sbomName);

    async function loadReachability() {
      for (const candidate of candidates) {
        try {
          const resp = await authFetch(`${config.BASE_URL}/sbom/${encodeURIComponent(repoName)}/${encodeURIComponent(candidate)}/reachability`);
          if (!resp.ok) continue;
          const payload = await resp.json();
          const findings = payload?.result?.findings || [];
          const map = {};
          findings.forEach(f => {
            const key = f.finding?.cveId || f.finding?.osvId;
            if (key) map[key] = f.reachability;
            // also index aliases
            if (f.finding?.cveId && f.finding?.osvId) map[f.finding.osvId] = f.reachability;
          });
          if (!cancelled) setReachMap(map);
          return;
        } catch { /* best effort */ }
      }
    }
    loadReachability();
    return () => { cancelled = true; };
  }, [repoName, sbomId, sbomName]);

  if (loading) return (
    <div style={{ padding: '1rem 0' }}>
      <Skeleton.Table rows={5} cols={7} />
    </div>
  );

  if (error) return <div className="alert alert-danger">{t('vulnerabilities.loadError')}: {error}</div>;
  if (!data)  return null;

  const hasReachability = Object.keys(reachMap).length > 0;
  const reachableCveIds = new Set(Object.entries(reachMap).filter(([, v]) => v === 'reachable').map(([k]) => k));

  const vulns = (data.vulnerabilities || []).filter(v => {
    if (filter !== 'ALL' && v.severity !== filter) return false;
    if (search && !v.packageName.toLowerCase().includes(search.toLowerCase()) &&
        !v.vulnId.toLowerCase().includes(search.toLowerCase()) &&
        !v.summary.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  // Sort: REACHABLE first, then by severity, then CVSS descending
  vulns.sort((a, b) => {
    const aReach = reachableCveIds.has(a.vulnId) ? 0 : 1;
    const bReach = reachableCveIds.has(b.vulnId) ? 0 : 1;
    if (aReach !== bReach) return aReach - bReach;
    const ao = (SEVERITY_CONFIG[a.severity] || SEVERITY_CONFIG.UNKNOWN).order;
    const bo = (SEVERITY_CONFIG[b.severity] || SEVERITY_CONFIG.UNKNOWN).order;
    if (ao !== bo) return ao - bo;
    return (b.cvssScore || 0) - (a.cvssScore || 0);
  });

  const totalFiltered = vulns.length;
  const nPages = Math.ceil(totalFiltered / pageSize);
  const pagedVulns = vulns.slice((page - 1) * pageSize, page * pageSize);

  /* Checkbox helpers */
  const toggleRow = (id, e) => {
    e.stopPropagation();
    setSelected(prev => { const s = new Set(prev); s.has(id) ? s.delete(id) : s.add(id); return s; });
  };
  const allPageSelected = pagedVulns.length > 0 && pagedVulns.every(v => selected.has(v.vulnId));
  const toggleSelectAll = () => {
    if (allPageSelected) {
      setSelected(prev => { const s = new Set(prev); pagedVulns.forEach(v => s.delete(v.vulnId)); return s; });
    } else {
      setSelected(prev => { const s = new Set(prev); pagedVulns.forEach(v => s.add(v.vulnId)); return s; });
    }
  };

  /* Bulk actions */
  async function bulkAcceptRisk() {
    setBulkLoading(true); setBulkMsg('');
    try {
      await Promise.all([...selected].map(vulnId =>
        authFetch(`${config.BASE_URL}/sbom/${repoName}/${sbomId}-sbom.json/remediation/${vulnId}`, {
          method: 'PUT', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ status: 'accepted_risk' }),
        })
      ));
      setBulkMsg(`✅ Marked ${selected.size} as Accepted Risk.`);
      setSelected(new Set());
    } catch (e) { setBulkMsg(`❌ ${e.message}`); }
    setBulkLoading(false);
  }

  async function bulkInTriage() {
    setBulkLoading(true); setBulkMsg('');
    try {
      await Promise.all([...selected].map(vulnId =>
        authFetch(`${config.BASE_URL}/sbom/${repoName}/${sbomId}-sbom.json/vex/${vulnId}`, {
          method: 'PUT', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ status: 'In Triage' }),
        })
      ));
      setBulkMsg(`✅ Added ${selected.size} to VEX as In Triage.`);
      setSelected(new Set());
    } catch (e) { setBulkMsg(`❌ ${e.message}`); }
    setBulkLoading(false);
  }

  return (
    <div>
      {/* Export + reachability hint */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem', flexWrap: 'wrap', gap: '0.5rem' }}>
        {hasReachability ? (
          <div style={{
            fontSize: '0.75rem', padding: '4px 12px', borderRadius: 20,
            background: 'rgba(239,68,68,0.1)', color: '#ef4444',
            border: '1px solid rgba(239,68,68,0.25)',
          }}>
            ⚠️ <strong>{reachableCveIds.size}</strong> reachable CVE{reachableCveIds.size !== 1 ? 's' : ''} — sorted to top
            &nbsp;·&nbsp;
            <span style={{ color: '#94a3b8' }}>Go to <em>Reachability</em> tab for Vulnerable Path Analysis</span>
          </div>
        ) : (
          <div style={{ fontSize: '0.72rem', color: '#94a3b8' }}>
            💡 Run a Reachability scan to see which CVEs are actually exploitable in your code
          </div>
        )}
        <button
          className="ce-btn ce-btn--secondary ce-btn--sm"
          onClick={() => window.open(authUrl(`${config.BASE_URL}/sbom/${repoName}/${sbomId}-sbom.json/export/vulnerabilities`))}
          title="Export vulnerabilities as CSV"
        >
          📥 {t('vulnerabilities.exportCsv')}
        </button>
      </div>

      {/* Summary Cards */}
      <div className="ce-kpi-row">
        {[
          { label: t('vulnerabilities.scanned', 'Scanned'), value: data.scannedPackages, color: '#6366f1' },
          { label: t('vulnerabilities.critical'), value: data.critical, color: '#dc2626' },
          { label: t('vulnerabilities.high'), value: data.high, color: '#ea580c' },
          { label: t('vulnerabilities.medium'), value: data.medium, color: '#ca8a04' },
          { label: t('vulnerabilities.low'), value: data.low, color: '#16a34a' },
        ].map(card => (
          <div key={card.label} className="ce-kpi" style={{ '--kpi-color': card.color }}>
            <div className="ce-kpi-value">{card.value}</div>
            <div className="ce-kpi-label">{card.label}</div>
          </div>
        ))}
      </div>

      {data.totalVulns === 0 ? (
        <div className="alert alert-success">
          {t('vulnerabilities.noVulnsClean', '✅ No known vulnerabilities found in {{count}} scanned packages.', { count: data.scannedPackages })}
        </div>
      ) : (
        <>
          {/* Filters */}
          <div className="ce-filter-bar">
            <input
              className="ce-search-input"
              placeholder={t('vulnerabilities.searchPlaceholder', 'Search package / CVE…')}
              value={search}
              onChange={e => setVulnSearch(e.target.value)}
            />
            {['ALL', 'CRITICAL', 'HIGH', 'MEDIUM', 'LOW'].map(sev => (
              <button
                key={sev}
                className={`ce-btn ce-btn--sm ${filter === sev ? 'ce-btn--primary' : 'ce-btn--secondary'}`}
                onClick={() => setVulnFilter(sev)}
              >
                {sev}
              </button>
            ))}
            <span className="ce-filter-count">{t('vulnerabilities.showingOf', 'Showing {{filtered}} of {{total}} vulnerabilities', { filtered: totalFiltered, total: data.totalVulns })}</span>
          </div>

          {/* Bulk action bar */}
          {selected.size > 0 && (
            <div style={{
              position: 'sticky', top: 0, zIndex: 100,
              display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
              background: '#1e293b', color: 'white',
              padding: '10px 16px', borderRadius: 8, marginBottom: 8,
            }}>
              <span style={{ fontWeight: 600, fontSize: '0.85rem' }}>{selected.size} selected</span>
              <button
                style={{ padding: '5px 14px', borderRadius: 6, border: 'none', cursor: 'pointer', background: '#dc2626', color: 'white', fontWeight: 600, fontSize: '0.8rem' }}
                onClick={bulkAcceptRisk} disabled={bulkLoading}
              >
                {bulkLoading ? '⏳' : '✅ Accept Risk'}
              </button>
              <button
                style={{ padding: '5px 14px', borderRadius: 6, border: 'none', cursor: 'pointer', background: '#6366f1', color: 'white', fontWeight: 600, fontSize: '0.8rem' }}
                onClick={bulkInTriage} disabled={bulkLoading}
              >
                {bulkLoading ? '⏳' : '🔵 Add to VEX (In Triage)'}
              </button>
              <button
                style={{ padding: '5px 14px', borderRadius: 6, border: '1px solid rgba(255,255,255,0.3)', cursor: 'pointer', background: 'transparent', color: 'white', fontSize: '0.8rem' }}
                onClick={() => { setSelected(new Set()); setBulkMsg(''); }}
              >
                Clear selection
              </button>
              {bulkMsg && (
                <span style={{ fontSize: '0.8rem', color: bulkMsg.startsWith('✅') ? '#86efac' : '#fca5a5' }}>
                  {bulkMsg}
                </span>
              )}
            </div>
          )}

          <div className="ce-table-wrap">
            <table className="ce-table ce-table--clickable">
              <thead>
                <tr>
                  <th style={{ width: 36, textAlign: 'center' }}>
                    <input
                      type="checkbox"
                      checked={allPageSelected}
                      onChange={toggleSelectAll}
                      title="Select all on this page"
                      style={{ cursor: 'pointer' }}
                    />
                  </th>
                  <th>{t('vulnerabilities.columns.severity')}</th>
                  <th>{t('vulnerabilities.columns.cvss')}</th>
                  <th>{t('vulnerabilities.columns.vulnId')}</th>
                  <th>{t('vulnerabilities.columns.package')}</th>
                  <th>{t('vulnerabilities.columns.version')}</th>
                  <th>{t('vulnerabilities.columns.fixedIn')}</th>
                  {hasReachability && <th>Reachability</th>}
                  <th>{t('vulnerabilities.columns.summary')}</th>
                </tr>
              </thead>
              <tbody>
                {pagedVulns.map((v, i) => {
                  const reachStatus = reachMap[v.vulnId] || reachMap[v.aliases?.[0]];
                  const isReachable = reachStatus === 'reachable';
                  const isSelected = selected.has(v.vulnId);
                  return (
                    <tr
                      key={`${v.vulnId}-${i}`}
                      onClick={() => setSelectedVuln(v)}
                      style={{
                        background: isSelected
                          ? 'rgba(99,102,241,0.07)'
                          : isReachable ? 'rgba(239,68,68,0.06)' : undefined,
                        cursor: 'pointer',
                      }}
                    >
                      <td style={{ textAlign: 'center' }} onClick={e => toggleRow(v.vulnId, e)}>
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => {}}
                          style={{ cursor: 'pointer' }}
                        />
                      </td>
                      <td><SeverityBadge severity={v.severity} /></td>
                      <td>
                        {v.cvssScore != null
                          ? <span className="fw-semibold small">{v.cvssScore.toFixed(1)}</span>
                          : <span className="text-muted small">—</span>}
                      </td>
                      <td>
                        <a href={v.osvUrl} target="_blank" rel="noreferrer" className="fw-bold" onClick={e => e.stopPropagation()}>
                          {v.vulnId}
                        </a>
                        {v.aliases && v.aliases.map(alias => (
                          <span key={alias} className="badge bg-light text-dark ms-1 small">{alias}</span>
                        ))}
                      </td>
                      <td>
                        <code>{v.packageName}</code>
                        {['apk', 'deb', 'rpm'].includes(getPURLType(v.purl)) && getDistroFromPURL(v.purl) && (
                          <span style={{
                            display: 'inline-block', marginLeft: '0.375rem',
                            padding: '0.1rem 0.45rem', borderRadius: 100,
                            fontSize: '0.68rem', fontWeight: 600,
                            background: 'rgba(14,165,233,0.1)', color: '#0284c7',
                            border: '1px solid rgba(14,165,233,0.25)',
                          }}>
                            {getDistroFromPURL(v.purl)}
                          </span>
                        )}
                      </td>
                      <td><code>{v.version}</code></td>
                      <td>
                        {v.fixedIn
                          ? <span className="ce-sev ce-sev--low">{v.fixedIn}</span>
                          : <span className="text-muted">—</span>
                        }
                      </td>
                      {hasReachability && <td><ReachBadge status={reachStatus} /></td>}
                      <td className="text-truncate" style={{ maxWidth: 300 }} title={v.summary}>
                        {v.summary}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <Pagination
            nPages={nPages}
            currentPage={page}
            setCurrentPage={setVulnPage}
            pageSize={pageSize}
            totalItems={totalFiltered}
          />
          {vulns.length === 0 && (
            <EmptyState
              icon="🛡️"
              title={filter === 'ALL' ? 'No vulnerabilities found' : `No ${filter} vulnerabilities`}
              description={filter === 'ALL'
                ? 'This SBOM has no known vulnerabilities. Keep dependencies up to date to stay secure.'
                : `No vulnerabilities match the current filter. Try selecting a different severity level.`}
              size="sm"
            />
          )}
        </>
      )}

      {/* CVE Detail Slide-Over Panel */}
      {selectedVuln && (
        <CveDetailPanel
          vuln={selectedVuln}
          repoName={repoName}
          sbomId={sbomId}
          reachMap={reachMap}
          onClose={() => setSelectedVuln(null)}
        />
      )}
    </div>
  );
};

export default VulnerabilityView;
